import { Check, ChevronsUpDown, X } from 'lucide-react';
import { FC, useState } from 'react';
import { cn } from '../../../utils/cn';
import { Button, ButtonProps } from '../../ui/button';
import { ComboboxOption, findOptionByValue, getOptionLabel, getOptionValue, optionsAreEquivalent } from '../../ui/combobox-shared';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { COUNTRY_OPTIONS } from '../constants/countries';

// Define the type for country flag data
interface CountryData {
  flagUrl: string;
}

// Type guard to check if option has country data
const isCountryOption = (option: ComboboxOption): option is {
  label: string;
  value: string | number;
  data: CountryData;
} => {
  return typeof option === 'object' && 'data' in option && option.data != null;
};

// Convert countries data to ComboboxOption format
const COUNTRY_OPTIONS_WITH_FLAGS: ComboboxOption<CountryData>[] = COUNTRY_OPTIONS.map(({
  label,
  value
}) => ({
  label,
  value,
  data: {
    flagUrl: `https://purecatamphetamine.github.io/country-flag-icons/3x2/${value}.svg`
  }
}));
export interface CountrySelectorProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  testIdKey?: string;
  size?: ButtonProps['size'];
  darkMode?: boolean;
  inDialog?: boolean;
  suppressDelete?: boolean;
}

// Custom country options component that displays flags
const CountryOptions: FC<{
  options: Array<ComboboxOption<CountryData>>;
  value?: string;
  onChange?: (value: string, data?: CountryData) => void;
  onClose: () => void;
}> = ({
  options,
  value,
  onChange,
  onClose
}) => {
  return <>
      {options.map(option => {
      const optionValue = getOptionValue(option);
      const optionLabel = getOptionLabel(option);
      const isSelected = value && optionsAreEquivalent(option, value);
      const flagUrl = isCountryOption(option) ? option.data.flagUrl : undefined;
      return <CommandItem key={optionValue} value={optionValue} onSelect={currentValue => {
        const nextValue = value && optionsAreEquivalent(currentValue, value) ? '' : currentValue;
        const selectedOption = findOptionByValue(options, nextValue);
        if (selectedOption) {
          onChange?.(getOptionValue(selectedOption), isCountryOption(selectedOption) ? selectedOption.data : undefined);
        }
        onClose();
      }} data-testid="country-option" className="flex items-center">
            <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
            {flagUrl && <img alt={`${optionValue} flag`} src={flagUrl} className="inline mr-2 h-4 rounded-sm" />}
            <span className="font-normal truncate">{optionLabel}</span>
          </CommandItem>;
    })}
    </>;
};
export default function CountrySelector({
  placeholder = 'Select a country',
  value,
  onChange,
  className,
  disabled = false,
  testIdKey,
  size,
  darkMode,
  inDialog = false,
  suppressDelete = false
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const activeOption = value ? findOptionByValue(COUNTRY_OPTIONS, value) : null;
  const selectedCountryData = activeOption && isCountryOption(activeOption) ? activeOption.data : null;
  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange?.('');
  };
  return <Popover open={open} onOpenChange={setOpen} modal={inDialog} data-sentry-element="Popover" data-sentry-component="CountrySelector" data-sentry-source-file="country-selector.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="country-selector.tsx">
        <Button variant="outline" role="combobox" aria-expanded={open} size={size} className={cn(className, 'cursor-default px-4 justify-between border-gray-200 rounded-lg border font-light text-sm', activeOption && !darkMode ? 'text-zinc-800' : 'text-gray-500', darkMode ? 'bg-gray-900 hover:text-zinc-200' : 'bg-white')} disabled={disabled} data-testid={`${testIdKey}-trigger`} data-sentry-element="Button" data-sentry-source-file="country-selector.tsx">
          <span className="w-full truncate text-left flex items-center">
            {activeOption ? <>
                {selectedCountryData?.flagUrl && <img alt={`${getOptionValue(activeOption)} flag`} src={selectedCountryData.flagUrl} className="inline mr-2 h-4 rounded-sm" />}
                {getOptionLabel(activeOption)}
              </> : placeholder}
          </span>
          {value && !suppressDelete && <X size={16} className="ml-2 shrink-0 opacity-70 hover:opacity-100" onClick={handleDelete} />}
          <ChevronsUpDown size={16} className="ml-2 shrink-0 opacity-50" data-sentry-element="ChevronsUpDown" data-sentry-source-file="country-selector.tsx" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(className, 'min-w-[var(--radix-popper-anchor-width)]')} data-sentry-element="PopoverContent" data-sentry-source-file="country-selector.tsx">
        <Command data-sentry-element="Command" data-sentry-source-file="country-selector.tsx">
          <CommandInput placeholder="Search countries..." data-sentry-element="CommandInput" data-sentry-source-file="country-selector.tsx" />
          <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="country-selector.tsx">No countries found</CommandEmpty>
          <CommandList data-sentry-element="CommandList" data-sentry-source-file="country-selector.tsx">
            <CountryOptions options={COUNTRY_OPTIONS_WITH_FLAGS} value={value} onChange={selectedValue => onChange?.(selectedValue)} onClose={() => setOpen(false)} data-sentry-element="CountryOptions" data-sentry-source-file="country-selector.tsx" />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>;
}

// Re-export types for convenience
export type { CountryData, ComboboxOption as CountryOption };